import { supabase } from '@/lib/supabase';

interface DocumentChunk {
  id: string;
  documentId: string;
  content: string;
  chunkIndex: number;
  totalChunks: number;
  embedding?: number[];
  metadata?: {
    startIndex: number;
    endIndex: number;
    overlap: number;
    language?: string;
    topic?: string;
    [key: string]: any;
  };
}

interface ChunkingOptions {
  maxChunkSize: number;
  overlapSize: number;
  preserveStructure: boolean;
  language: string;
}

/**
 * Split a document into overlapping chunks for better knowledge retrieval
 */
export function chunkDocument(
  documentId: string,
  content: string,
  options: ChunkingOptions = {
    maxChunkSize: 1000,
    overlapSize: 200,
    preserveStructure: true,
    language: 'en'
  }
): DocumentChunk[] {
  const { maxChunkSize, overlapSize, preserveStructure, language } = options;
  
  if (!content || content.length <= maxChunkSize) {
    return [{
      id: `${documentId}_chunk_0`,
      documentId,
      content,
      chunkIndex: 0,
      totalChunks: 1,
      metadata: {
        startIndex: 0,
        endIndex: content.length,
        overlap: 0,
        language
      }
    }];
  }
  
  const chunks: DocumentChunk[] = [];
  let startIndex = 0;
  let chunkIndex = 0;
  
  // Split by structure if preserveStructure is true
  const splitPoints = preserveStructure ? findStructuralSplitPoints(content) : [];
  
  while (startIndex < content.length) {
    let endIndex = Math.min(startIndex + maxChunkSize, content.length);
    
    // Try to find a good split point near the target end
    if (preserveStructure && endIndex < content.length) {
      const nearSplitPoint = splitPoints.find(point => 
        point >= startIndex + (maxChunkSize * 0.7) && point <= endIndex
      );
      
      if (nearSplitPoint) {
        endIndex = nearSplitPoint;
      } else {
        // Fall back to sentence boundary
        const sentenceEnd = findSentenceEnd(content, endIndex);
        if (sentenceEnd > startIndex && sentenceEnd < content.length) {
          endIndex = sentenceEnd;
        }
      }
    }
    
    const chunkContent = content.substring(startIndex, endIndex).trim();
    
    if (chunkContent.length > 0) {
      chunks.push({
        id: `${documentId}_chunk_${chunkIndex}`,
        documentId,
        content: chunkContent,
        chunkIndex,
        totalChunks: 0, // Will be updated later
        metadata: {
          startIndex,
          endIndex,
          overlap: chunkIndex > 0 ? overlapSize : 0,
          language
        }
      });
      
      chunkIndex++;
    }
    
    // Move start index with overlap
    startIndex = Math.max(startIndex + 1, endIndex - overlapSize);
  }
  
  // Update total chunks count
  chunks.forEach(chunk => {
    chunk.totalChunks = chunks.length;
  });
  
  console.log(`[CHUNKING] Split document ${documentId} into ${chunks.length} chunks`);
  return chunks;
}

/**
 * Find structural split points (paragraphs, sections, etc.)
 */
function findStructuralSplitPoints(content: string): number[] {
  const splitPoints: number[] = [];
  
  // Find paragraph breaks
  const paragraphRegex = /\n\s*\n/g;
  let match;
  while ((match = paragraphRegex.exec(content)) !== null) {
    splitPoints.push(match.index + match[0].length);
  }
  
  // Find section headers
  const headerRegex = /\n#+\s/g;
  while ((match = headerRegex.exec(content)) !== null) {
    splitPoints.push(match.index);
  }
  
  // Find list items
  const listRegex = /\n[-*•]\s/g;
  while ((match = listRegex.exec(content)) !== null) {
    splitPoints.push(match.index);
  }
  
  return splitPoints.sort((a, b) => a - b);
}

/**
 * Find the end of the nearest sentence
 */
function findSentenceEnd(content: string, startIndex: number): number {
  const sentenceEnders = ['.', '!', '?', '。', '！', '？'];
  
  for (let i = startIndex; i < Math.min(startIndex + 100, content.length); i++) {
    if (sentenceEnders.includes(content[i])) {
      return i + 1;
    }
  }
  
  return startIndex;
}

/**
 * Generate embeddings for document chunks
 */
export async function generateChunkEmbeddings(
  chunks: DocumentChunk[],
  onProgress?: (completed: number, total: number) => void
): Promise<DocumentChunk[]> {
  const chunksWithEmbeddings: DocumentChunk[] = [];
  
  for (let i = 0; i < chunks.length; i++) {
    try {
      const { data, error } = await supabase.functions.invoke('generate-embedding', {
        body: { text: chunks[i].content }
      });
      
      if (!error && data?.embedding) {
        chunksWithEmbeddings.push({
          ...chunks[i],
          embedding: data.embedding
        });
      } else {
        console.error(`Failed to generate embedding for chunk ${i}:`, error);
        chunksWithEmbeddings.push(chunks[i]);
      }
      
      if (onProgress) {
        onProgress(i + 1, chunks.length);
      }
      
      // Rate limiting
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error(`Error generating embedding for chunk ${i}:`, error);
      chunksWithEmbeddings.push(chunks[i]);
    }
  }
  
  return chunksWithEmbeddings;
}

/**
 * Store chunks in knowledge base
 */
export async function storeDocumentChunks(
  chunks: DocumentChunk[],
  topic: string,
  parentMetadata?: any
): Promise<boolean> {
  try {
    const chunkRecords = chunks.map(chunk => ({
      id: chunk.id,
      document: {
        content: chunk.content,
        chunk_info: {
          document_id: chunk.documentId,
          chunk_index: chunk.chunkIndex,
          total_chunks: chunk.totalChunks,
          is_chunk: true
        }
      },
      embedding: chunk.embedding,
      metadata: {
        ...chunk.metadata,
        ...parentMetadata,
        topic,
        is_chunk: true,
        parent_document_id: chunk.documentId
      }
    }));
    
    const { error } = await supabase
      .from('assistant_knowledge')
      .insert(chunkRecords);
    
    if (error) {
      console.error('Error storing document chunks:', error);
      return false;
    }
    
    console.log(`Successfully stored ${chunks.length} chunks for topic: ${topic}`);
    return true;
  } catch (error) {
    console.error('Error in storeDocumentChunks:', error);
    return false;
  }
}
